/**
 * Returns `raw` only when it is a genuinely same-origin path — i.e. starts with
 * a single `/` and cannot be coerced into an off-site navigation. Anything else
 * (absolute URLs, protocol-relative `//host`, backslash/control-char obfuscated
 * variants, schemeless values like `evil.com`, null/undefined/empty) falls back
 * to `fallback`.
 *
 * Used by `/login`, `/signup`, and `/awaiting-verification` to honour an
 * optional `?redirect=` query without becoming an open-redirect phishing
 * primitive: a value that escapes the origin would otherwise let an attacker
 * craft `/login?redirect=https://evil.com` and bounce the victim off-site
 * immediately after they enter credentials.
 */
export function safeRedirectTarget(
  raw: string | null | undefined,
  fallback: string,
): string {
  if (!raw) return fallback;
  // Strip all whitespace. The WHATWG URL parser (used by the browser and Next's
  // router) silently deletes tab/LF/CR from URLs, so a value like `/\t//evil.com`
  // would otherwise slip past the `//` check below and resolve to a
  // protocol-relative off-site URL at navigation time. `\s` covers tab/LF/CR,
  // space, and U+2028/U+2029. Real redirect paths carry no raw whitespace
  // (spaces are %20-encoded), so this is loss-free for legitimate values.
  const cleaned = raw.replace(/\s+/g, '');
  if (!cleaned.startsWith('/')) return fallback;
  // Reject protocol-relative (`//host`) and backslash-obfuscated variants
  // (`/\evil.com`, `/\/evil.com`) — browsers normalise `\` to `/` for http(s).
  if (/^\/[/\\]/.test(cleaned)) return fallback;
  // Belt-and-braces: resolve against a throwaway origin and confirm the result
  // stays same-origin with a path that still starts with `/`. Anything that
  // escapes the origin (userinfo `@`, embedded scheme, control chars, etc.)
  // falls back.
  try {
    const u = new URL(cleaned, 'https://internal.invalid');
    if (u.origin !== 'https://internal.invalid' || !u.pathname.startsWith('/')) {
      return fallback;
    }
  } catch {
    return fallback;
  }
  return cleaned;
}

/**
 * `path` with the same `?redirect=` carried along, when there is a safe one to
 * carry; otherwise `path` unchanged.
 *
 * For the links BETWEEN the sign-in pages (/signup's "Log In", /login's
 * "Sign Up", /awaiting-verification sending a signed-out visitor back to
 * /signup). Each page honours the redirect on its own, so a link that dropped
 * it lost the buyer's way back the moment they switched page, which is the
 * common case: someone sent to "Create an account" who already has one.
 */
export function withRedirect(path: string, raw: string | null | undefined): string {
  const target = safeRedirectTarget(raw, '');
  if (!target) return path;
  return `${path}${path.includes('?') ? '&' : '?'}redirect=${encodeURIComponent(target)}`;
}
